import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import React, { useState } from "react";
import SendReceiptContent from "./send-receipt-content/SendReceiptContent";
import RefundTransactionContent from "./refund-transaction-content/RefundTransactionContent";
import { trimObject } from "utils/utils";
import { emailFormatValidator } from "utils/formValidation";
import { useSnackbar } from "notistack";
import {
  getTransactionRefundableOnline,
  refundTransactionOffline,
  refundTransactionOnline,
  sendReceiptFormOffline,
  sendReceiptFormOnline,
  voidTransactionFormOffline,
  voidTransactionFormOnline,
} from "./model";
import { useTranslation } from "react-i18next";
import TextMAS from "components/TextMAS/TextMAS";
import Spin from "components/Spin/Spin";
import { useAuth } from "utils/useAuth/useAuth";

const COMPONENT_LOCALE_KEY = "page.transactions.sharedComponents.";

const SEND_RECEIPT_DIALOG_INIT_STATE = {
  transactionId: "",

  //offline
  email: "",
  receiptType: "CUSTOMER",

  //online
  merchId: "",
  orderId: "",
  email: "",
};

const VOID_TRANSACTION_DIALOG_INIT_STATE = {
  transactionId: "",
};

const REFUND_TRANSACTION_DIALOG_INIT_STATE = {
  transactionId: "",
  refundAmount: "",
  confirmAmount: "",
};

const REFUND_TRANSACTION_DIALOG_ERROR_INIT_STATE = {
  refundAmount: false,
  confirmAmount: false,
  valueMismatch: false,
  valueExceeded: false,
};

const useTransactionActionDialogs = ({
  env,
  dialogs = ["all"],
  datasource,
  handleRefetch,
}) => {
  const isOffline = String(env).toLowerCase() !== "online";
  const hasReceipt = dialogs.some((e) => ["receipt", "all"].includes(e));
  const hasVoid = dialogs.some((e) => ["void", "all"].includes(e));
  const hasRefund = dialogs.some((e) => ["refund", "all"].includes(e));

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { logout } = useAuth();

  const [isReceiptSending, setIsReceiptSending] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);

  const [isSendReceiptDialogOpen, setIsSendReceiptDialogOpen] = useState(false);
  const [sendReceiptDialogData, setSendReceiptDialogData] = useState(
    SEND_RECEIPT_DIALOG_INIT_STATE,
  );

  const [isVoidTransactionDialogOpen, setIsVoidTransactionDialogOpen] =
    useState(false);
  const [VoidTransactionDialogData, setVoidTransactionDialogData] = useState(
    VOID_TRANSACTION_DIALOG_INIT_STATE,
  );

  const [isRefundTransactionDialogOpen, setIsRefundTransactionDialogOpen] =
    useState(false);
  const [RefundTransactionDialogData, setRefundTransactionDialogData] =
    useState(REFUND_TRANSACTION_DIALOG_INIT_STATE);
  const [RefundTransactionDialogError, setRefundTransactionDialogError] =
    useState(REFUND_TRANSACTION_DIALOG_ERROR_INIT_STATE);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  // ===================== send receipt dialog function ===================== //
  const openSendReceiptDialog = (transaction) => {
    restoreSendReceiptFormInput();
    if (isOffline) {
      setSendReceiptDialogData((prev) => ({
        ...prev,
        transactionId: transaction.id,
      }));
    } else {
      setSendReceiptDialogData((prev) => ({
        ...prev,
        transactionId: transaction.id,
        merchId: transaction.merchantId,
        orderId: transaction.order_id,
      }));
    }
    setIsSendReceiptDialogOpen(true);
  };

  const closeSendReceiptDialog = () => {
    setIsSendReceiptDialogOpen(false);
    setIsReceiptSending(false);
  };

  const handleSendReceiptFormInput = (data, field) => {
    setSendReceiptDialogData({
      ...sendReceiptDialogData,
      [field]: data,
    });
  };

  const restoreSendReceiptFormInput = () => {
    setSendReceiptDialogData(SEND_RECEIPT_DIALOG_INIT_STATE);
  };

  const sendReceiptContent = () => {
    return (
      <SendReceiptContent
        isOffline={isOffline}
        handleInput={handleSendReceiptFormInput}
        data={sendReceiptDialogData}
        isLoading={isReceiptSending}
      />
    );
  };

  const handleSendReceiptFormAction = async () => {
    setIsReceiptSending(true);
    const payload = isOffline
      ? trimObject({
        ...sendReceiptDialogData,
        ...sendReceiptDialogData.receiptType === "CUSTOMER" && { customerEmail: sendReceiptDialogData.email },
        ...sendReceiptDialogData.receiptType === "MERCHANT" && { merchantEmail: sendReceiptDialogData.email },
        email: undefined, //If no customerEmail/merchantEmail is provided, will send merchant receipt to merchant email as default
        receiptType: undefined,
      })
      : trimObject({
        ...sendReceiptDialogData,
        receiptType: undefined,
      });
    const apiResponse = await (isOffline
      ? sendReceiptFormOffline(payload)
      : sendReceiptFormOnline(payload));

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t(COMPONENT_LOCALE_KEY + "sendReceipt.successMessage"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t(COMPONENT_LOCALE_KEY + "sendReceipt.failMessage"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmSendReceipt = async () => {
    const email = sendReceiptDialogData.email;

    // Skip checking email presence if offline env and receipt type is MERCHANT
    if (!isOffline || (isOffline && sendReceiptDialogData.receiptType === "CUSTOMER")) {
      if (!email) {
        snackbar(
          t(COMPONENT_LOCALE_KEY + "sendReceipt.errorMessage.1"),
          "warning",
        );
        return;
      }
    }

    if (email && !emailFormatValidator(email)) {
      snackbar(
        t(COMPONENT_LOCALE_KEY + "sendReceipt.errorMessage.2"),
        "warning",
      );
      return;
    }

    await handleSendReceiptFormAction();
    restoreSendReceiptFormInput();
    closeSendReceiptDialog();
  };
  // ===================== send receipt dialog function end ===================== //

  // ===================== void transaction dialog function ===================== //
  const openVoidTransactionDialog = (data) => {
    handleVoidTransactionFormInput(data.id, "transactionId");
    setIsVoidTransactionDialogOpen(true);
  };

  const closeVoidTransactionDialog = () => {
    restoreVoidTransactionFormInput();
    setIsVoidTransactionDialogOpen(false);
    setIsVoiding(false);
  };

  const handleVoidTransactionFormInput = (data, field) => {
    setVoidTransactionDialogData({
      ...VoidTransactionDialogData,
      [field]: data,
    });
  };

  const restoreVoidTransactionFormInput = () => {
    setVoidTransactionDialogData(VOID_TRANSACTION_DIALOG_INIT_STATE);
  };
  const voidTransactionContent = () => {
    return (
      <div>
        <TextMAS fontSize="16px">
          {t(COMPONENT_LOCALE_KEY + "voidTransaction.dialog.content")}
        </TextMAS>
        {isVoiding && (
          <div
            style={{
              position: "relative",
              minHeight: "46px",
              marginTop: "18px",
            }}
          >
            <Spin type={"search"} />
          </div>
        )}
      </div>
    );
  };
  const handleVoidTransactionFormAction = async () => {
    setIsVoiding(true);
    const transaction = datasource?.transaction.data.find(
      (item) => item.id === VoidTransactionDialogData.transactionId,
    );
    const payload = isOffline
      ? trimObject({
        transactionId: transaction.id,
        employee: {
          employeeId: transaction.employeeId,
          merchantId: transaction.merchantId,
        },
        transactionType: transaction.transactionType,
      })
      : trimObject({
        orderId: transaction.order_id,
        merchId: transaction.merchantId,
      });
    const apiResponse = await (isOffline
      ? voidTransactionFormOffline(payload)
      : voidTransactionFormOnline(payload));

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t(COMPONENT_LOCALE_KEY + "voidTransaction.successMessage"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t(COMPONENT_LOCALE_KEY + "voidTransaction.failMessage"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmVoidTransaction = async () => {
    await handleVoidTransactionFormAction();
    handleRefetch();
    restoreVoidTransactionFormInput();
    closeVoidTransactionDialog();
  };
  // ===================== void transaction dialog function end ===================== //

  // ===================== refund transaction dialog function ===================== //
  const openRefundTransactionDialog = (data) => {
    handleRefundTransactionFormInput(data.id, "transactionId");
    setIsRefundTransactionDialogOpen(true);
  };

  const closeRefundTransactionDialog = () => {
    restoreRefundTransactionFormInput();
    setIsRefundTransactionDialogOpen(false);
  };

  const handleRefundTransactionFormInput = (data, field) => {
    setRefundTransactionDialogData({
      ...RefundTransactionDialogData,
      [field]: data,
    });
  };

  const restoreRefundTransactionFormInput = () => {
    setRefundTransactionDialogData(REFUND_TRANSACTION_DIALOG_INIT_STATE);
    setRefundTransactionDialogError(REFUND_TRANSACTION_DIALOG_ERROR_INIT_STATE);
  };

  const resetRefundTransactionFormError = () => {
    setRefundTransactionDialogError(REFUND_TRANSACTION_DIALOG_ERROR_INIT_STATE);
  };

  const refundTransactionContent = () => {
    return (
      <RefundTransactionContent
        handleInput={handleRefundTransactionFormInput}
        data={RefundTransactionDialogData}
        error={RefundTransactionDialogError}
        isRefunding={isRefunding}
      />
    );
  };

  const btnRefundPressed = () => {
    if (handleRefundTransaction()) {
      handleConfirmRefundTransaction();
    }
  };

  const handleRefundTransactionFormAction = async () => {
    setIsRefunding(true);

    if (!isOffline) {
      /*
       * MAS-591 Check refundable is now handled by backend for offline
       * Only check refundable for online
       */
      const canRefundResponse = await getTransactionRefundableOnline(
        trimObject({
          amount: RefundTransactionDialogData.refundAmount,
          currencyCode: datasource?.payment?.data[0]?.["currency"],
          merchantId: datasource?.payment?.data[0]?.["merchantId"],
        })
      );

      if (!canRefundResponse || !canRefundResponse?.data) {
        snackbar(t("general.message.networkError"), "error");
        return false;
      } else {
        switch (canRefundResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            return false;
          }
          default: {
            const responseBody = canRefundResponse.data;
            if (!responseBody.message) {
              // what the fuck????
              snackbar(
                t(
                  COMPONENT_LOCALE_KEY +
                  "refundTransaction.confirmDialog.failMessage.online.refundable",
                ),
                "error",
              );
              return false;
            }
          }
        }
      }
    }

    const transaction = datasource?.transaction.data.find(
      (item) => item.id === RefundTransactionDialogData.transactionId,
    );
    const refundApiResponse = await (isOffline
      ? refundTransactionOffline(
        trimObject({
          transactionId: transaction.id,
          employee: {
            employeeId: transaction.employeeId,
            merchantId: transaction.merchantId,
          },
          amountOfTransaction: Number(
            RefundTransactionDialogData.refundAmount,
          ),
        }),
      )
      : refundTransactionOnline(
        trimObject({
          orderId: transaction.order_id,
          merchId: transaction.merchantId,
          transactionAmount: Number(RefundTransactionDialogData.refundAmount),
        }),
      ));

    if (!refundApiResponse || !refundApiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (refundApiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = refundApiResponse.data;
          switch (responseBody.responseCode) {
            case "0000": {
              snackbar(
                t(
                  COMPONENT_LOCALE_KEY +
                  "refundTransaction.confirmDialog.successMessage",
                ),
                "success",
              );
              return true;
            }
            case "0918": {
              snackbar(
                t(
                  COMPONENT_LOCALE_KEY +
                  "refundTransaction.confirmDialog.failMessage.insufficientFunds",
                ),
                "error",
              );
              return false;
            }
            default: {
              snackbar(
                t(
                  COMPONENT_LOCALE_KEY +
                  "refundTransaction.confirmDialog.failMessage.error",
                ),
                "error",
              );
              return false;
            }
          }
        }
      }
    }
  };

  const handleRefundTransaction = () => {
    resetRefundTransactionFormError();
    let validFlag = true;
    if (
      !(
        RefundTransactionDialogData.refundAmount &&
        !isNaN(RefundTransactionDialogData.refundAmount)
      )
    ) {
      setRefundTransactionDialogError((error) => ({
        ...error,
        refundAmount: true,
      }));
      validFlag = false;
    }
    if (
      !(
        RefundTransactionDialogData.confirmAmount &&
        !isNaN(RefundTransactionDialogData.confirmAmount)
      )
    ) {
      setRefundTransactionDialogError((error) => ({
        ...error,
        confirmAmount: true,
      }));
      validFlag = false;
    }

    if (
      RefundTransactionDialogData.refundAmount !==
      RefundTransactionDialogData.confirmAmount
    ) {
      setRefundTransactionDialogError((error) => ({
        ...error,
        valueMismatch: true,
      }));
      validFlag = false;
    }

    if (
      RefundTransactionDialogData.refundAmount >
      datasource?.payment?.data[0]?.["netAmount"] ??
      0
    ) {
      setRefundTransactionDialogError((error) => ({
        ...error,
        valueExceeded: true,
      }));
      validFlag = false;
    }

    return validFlag;
  };

  const handleConfirmRefundTransaction = async () => {
    const refundSuccess = await handleRefundTransactionFormAction();
    setIsRefunding(false);
    if (refundSuccess) {
      handleRefetch();
      restoreRefundTransactionFormInput();
      closeRefundTransactionDialog();
    }
  };
  // ===================== refund transaction dialog function end ===================== //

  const dialogComponents = [
    hasReceipt && (
      <SimpleDialog
        key={"transaction-action-dialog-receipt"}
        open={isSendReceiptDialogOpen}
        dialogOncloseFn={closeSendReceiptDialog}
        title={t(COMPONENT_LOCALE_KEY + "sendReceipt.dialog.title")}
        content={sendReceiptContent()}
        confirmFn={!isReceiptSending ? confirmSendReceipt : () => { }}
        positiveAction={t("component.simple.dialog.action.7")}
        positiveActionDisabled={isReceiptSending}
        negativeAction={t("component.simple.dialog.action.4")}
        negativeActionDisabled={isReceiptSending}
      />
    ),
    hasVoid && (
      <SimpleDialog
        key={"transaction-action-dialog-void"}
        open={isVoidTransactionDialogOpen}
        dialogOncloseFn={closeVoidTransactionDialog}
        title={t(COMPONENT_LOCALE_KEY + "voidTransaction.dialog.title")}
        content={voidTransactionContent()}
        confirmFn={!isVoiding ? confirmVoidTransaction : () => { }}
        positiveAction={t("component.simple.dialog.action.3")}
        positiveActionDisabled={isVoiding}
        negativeAction={t("component.simple.dialog.action.4")}
        negativeActionDisabled={isVoiding}
      />
    ),
    hasRefund && (
      <SimpleDialog
        key={"transaction-action-dialog-refund"}
        open={isRefundTransactionDialogOpen}
        dialogOncloseFn={closeRefundTransactionDialog}
        title={t(
          COMPONENT_LOCALE_KEY +
          "refundTransaction.dialog." +
          (isOffline ? "offline" : "online") +
          ".title",
        )}
        content={refundTransactionContent()}
        confirmFn={!isRefunding ? btnRefundPressed : () => { }}
        positiveAction={t("component.simple.dialog.action.3")}
        positiveActionDisabled={isRefunding}
        negativeAction={t("component.simple.dialog.action.4")}
        negativeActionDisabled={isRefunding}
      />
    ),
  ].filter((e) => e);
  return {
    TransactionActionDialogComponents: dialogComponents,
    openReceiptDialog: openSendReceiptDialog,
    openVoidDialog: openVoidTransactionDialog,
    openRefundDialog: openRefundTransactionDialog,
  };
};

export default useTransactionActionDialogs;
